import { motion } from "framer-motion";
import { MapPin, Scale, Sparkles, RefreshCw, Handshake, ShieldCheck } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { Eyebrow } from "@/components/Motion";
import LeafDecor from "@/components/LeafDecor";

const ICONS = { MapPin, Scale, Sparkles, RefreshCw, Handshake, ShieldCheck };
const EASE = [0.22, 1, 0.36, 1];

export default function Differentiation() {
  const { c } = useLang();
  const d = c.difference;
  return (
    <section id="difference" data-testid="difference-section" className="relative py-28 sm:py-40 bg-[#E7E2D1] overflow-hidden">
      <LeafDecor layers={[
        { img: "side", className: "top-1/2 right-0 -translate-y-1/2 h-[640px] -mr-28 rotate-6", opacity: 0.34 },
        { img: "corner", className: "bottom-0 left-0 w-[420px] -ml-20 -mb-16 rotate-[200deg]", opacity: 0.3 },
        { img: "bunch", className: "top-0 right-1/3 w-[220px] -mt-10 rotate-6 hidden lg:block", opacity: 0.18 },
      ]} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <Eyebrow testid="difference-eyebrow">{d.eyebrow}</Eyebrow>
        <div className="mt-6">
          <h2 className="font-serif font-light leading-none tracking-[-0.02em] text-forest text-5xl sm:text-6xl lg:text-7xl">
            {d.heading.pre}<span className="text-gold italic">{d.heading.em}</span>
          </h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-forest/10 bg-white/50 backdrop-blur-[2px]">
          {d.pillars.map((p, i) => {
            const Icon = ICONS[p.icon];
            return (
              <motion.div
                key={i}
                data-testid={`pillar-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-6%" }}
                transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
                className="group relative border-r border-b border-forest/10 p-8 sm:p-10 min-h-[240px] flex flex-col justify-between overflow-hidden"
              >
                <div className="absolute inset-0 bg-leaf/[0.07] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-start justify-between">
                  <Icon size={26} strokeWidth={1.4} className="text-leaf" />
                  <span className="font-serif text-2xl text-forest/20 group-hover:text-gold transition-colors duration-500">0{i + 1}</span>
                </div>
                <div className="relative">
                  <h3 className="font-serif text-2xl sm:text-3xl text-forest mb-3">{p.title}</h3>
                  <p className="font-sans text-sm leading-relaxed text-forest/90">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
